// frontend/src/router.jsx
//
// Central route registry — the single source of truth for navigation.
//
// Architecture decisions:
//
// 1. React Router v6 createBrowserRouter:
//    Uses the History API (no hash in URLs). More powerful than the old
//    <BrowserRouter> approach — supports loaders, actions, error boundaries.
//
// 2. React.lazy + Suspense (code splitting):
//    Each module is loaded only when the user actually navigates to it.
//    This keeps the initial bundle small regardless of how many modules exist.
//    Vite automatically splits lazy imports into separate chunks.
//
// 3. Route metadata (label, showInSidebar):
//    Keeping route config adjacent to the router means the Sidebar
//    never needs to know about individual modules — it just reads this list.
//
// HOW TO ADD A NEW MODULE:
//   1. Create frontend/src/modules/<module_name>/index.jsx
//   2. Add a lazy import below (follow the existing pattern)
//   3. Add an entry to MODULE_ROUTES with path, labelKey, showInSidebar
//      and add nav.<slug> to frontend/src/i18n/locales/{ru,en}.json
//   4. Import module locales in frontend/src/i18n/index.js
//   5. Rebuild (or hot-reload picks it up in dev)

import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from './components/Layout/Layout'

// ── Lazy module imports ────────────────────────────────────────────────────────
// Each import() call is a separate Rollup/Vite chunk — loaded on demand.
const Landing = lazy(() => import('./modules/landing/index'))
const QuizTrainer = lazy(() => import('./modules/quiz_trainer/index'))
const QuizTrainerQuiz = lazy(() =>
  import('./modules/quiz_trainer/index').then((m) => ({ default: m.QuizTrainerQuiz }))
)
// TODO: Add new module imports here, e.g.:
// const Blog    = lazy(() => import('./modules/blog/index'))
// const Projects = lazy(() => import('./modules/projects/index'))

// Suspense fallback — uses i18n so loading text switches language too
function SuspenseFallback() {
  const { t } = useTranslation()
  return <div className="loading">{t('loading')}</div>
}

// ── Route definitions ─────────────────────────────────────────────────────────
// This array drives BOTH the router and the Sidebar navigation.
//   path          — URL path (relative to root)
//   labelKey      — i18n key in common namespace (nav.xxx); translated by Sidebar
//   element       — lazy component
//   showInSidebar — whether to show a link in the sidebar menu
export const MODULE_ROUTES = [
  {
    path: '/',
    labelKey: 'nav.home',
    element: Landing,
    showInSidebar: true,
  },
  {
    path: '/quiz-trainer',
    labelKey: 'nav.quiz_trainer',
    element: QuizTrainer,
    showInSidebar: true,
  },
  {
    path: '/quiz-trainer/topics/:topicId',
    labelKey: 'nav.quiz_trainer',
    element: QuizTrainerQuiz,
    showInSidebar: false,
  },
  // TODO: Add new module routes here, e.g.:
  // {
  //   path: '/blog',
  //   labelKey: 'nav.blog',  // also add nav.blog to src/i18n/locales/{ru,en}.json
  //   element: Blog,
  //   showInSidebar: true,
  // },
]

// ── Router ────────────────────────────────────────────────────────────────────
// Layout is the persistent shell (sidebar + main area).
// Each module renders inside <Outlet /> in Layout.
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: MODULE_ROUTES.map(({ path, element: Component }) => ({
      path: path === '/' ? undefined : path,
      index: path === '/',
      element: (
        <Suspense fallback={<SuspenseFallback />}>
          <Component />
        </Suspense>
      ),
    })),
  },
])
